import { state } from "../store";
import { setRoot, pad2, showError } from "../render";
import { fetchMonthAvailability } from "../api";
import type { DayAvailability } from "../types";

// 表示中の月（年・月）。先頭表示は本日の月。
let viewYear: number;
let viewMonth: number; // 0-indexed (Date 互換)

// 過去月へは戻れない・未来は本日から最大3ヶ月先まで
const MAX_FUTURE_MONTHS = 3;

export async function renderStep1() {
  const today = new Date();
  if (viewYear === undefined || viewMonth === undefined) {
    viewYear = today.getFullYear();
    viewMonth = today.getMonth();
  }
  await renderMonth();
}

function isAtMinMonth(): boolean {
  const today = new Date();
  return viewYear === today.getFullYear() && viewMonth === today.getMonth();
}

function isAtMaxMonth(): boolean {
  const today = new Date();
  const maxDate = new Date(today.getFullYear(), today.getMonth() + MAX_FUTURE_MONTHS, 1);
  return viewYear === maxDate.getFullYear() && viewMonth === maxDate.getMonth();
}

async function renderMonth() {
  const monthStr = `${viewYear}-${pad2(viewMonth + 1)}`;
  const prevDisabled = isAtMinMonth();
  const nextDisabled = isAtMaxMonth();

  // Phase 1.5.4 拡張：reschedule モード時は表記を変えて「日時変更中」と分かるようにする
  const isReschedule = state.reschedule !== null;
  const titleText = isReschedule ? "🔄 新しい日付を選択" : "📅 日付を選択";
  const stepText = isReschedule ? "Step 1 / 2（変更）" : "Step 1 / 5";
  const oldInfoBanner = isReschedule
    ? `
      <div class="mt-3 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800 text-center">
        変更前の予約：${formatJpDate(state.reschedule!.reservation_date)} ${state.reschedule!.reservation_time}
      </div>
    `
    : "";

  setRoot(`
    <header class="text-center pb-3 border-b">
      <h1 class="font-bold text-lg">${titleText}</h1>
      <p class="text-xs text-gray-500 mt-1">${stepText}</p>
    </header>
    ${oldInfoBanner}
    <div class="mt-4 flex items-center justify-between">
      <button id="prev-month-btn"
        class="px-3 py-2 text-sm rounded ${prevDisabled ? "text-gray-300 cursor-not-allowed" : "text-emerald-700 hover:bg-emerald-50"}"
        ${prevDisabled ? "disabled" : ""}>
        ‹ 前月
      </button>
      <div class="font-bold">${viewYear}年${viewMonth + 1}月</div>
      <button id="next-month-btn"
        class="px-3 py-2 text-sm rounded ${nextDisabled ? "text-gray-300 cursor-not-allowed" : "text-emerald-700 hover:bg-emerald-50"}"
        ${nextDisabled ? "disabled" : ""}>
        来月 ›
      </button>
    </div>
    <div id="calendar-grid" class="mt-3 grid grid-cols-7 gap-1 text-center text-xs">
      <div class="text-red-600">日</div><div>月</div><div>火</div><div>水</div><div>木</div><div>金</div><div class="text-blue-600">土</div>
      <div class="col-span-7 text-gray-400 mt-2">読み込み中...</div>
    </div>
    <div class="mt-6 text-center">
      <button id="btn-go-my-reservations"
        class="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-base font-semibold px-8 py-3 rounded-lg shadow-md">
        📋 予約確認
      </button>
    </div>
    <div class="mt-auto pt-4 text-center text-xs text-gray-500">
      日付をタップすると次の画面に進みます
    </div>
  `);

  // 「マイ予約」へ遷移するリンク
  document.getElementById("btn-go-my-reservations")?.addEventListener("click", async () => {
    const { renderMyReservations } = await import("../views/myReservations");
    await renderMyReservations();
  });

  // ナビゲーションボタン
  if (!prevDisabled) {
    document.getElementById("prev-month-btn")!.addEventListener("click", () => {
      changeMonth(-1);
    });
  }
  if (!nextDisabled) {
    document.getElementById("next-month-btn")!.addEventListener("click", () => {
      changeMonth(1);
    });
  }

  let days: DayAvailability[] = [];
  try {
    days = await fetchMonthAvailability(monthStr);
  } catch (e: any) {
    showError(e?.message ?? "通信エラー");
    return;
  }

  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const grid = document.getElementById("calendar-grid")!;
  grid.innerHTML = `
    <div class="text-red-600">日</div><div>月</div><div>火</div><div>水</div><div>木</div><div>金</div><div class="text-blue-600">土</div>
  `;
  for (let i = 0; i < firstDow; i++) {
    const blank = document.createElement("div");
    blank.className = "text-gray-300";
    grid.appendChild(blank);
  }
  for (const d of days) {
    const cell = document.createElement("button");
    cell.dataset.date = d.date;
    // 日付（DD）を表示し、空き状況は背景色で表現する。
    // "2026-05-15" のような ISO 形式から日数だけを取り出す。
    const dayNum = Number(d.date.split("-")[2]);
    cell.textContent = String(dayNum);
    if (d.available) {
      cell.className = "bg-green-500 text-white rounded py-2 font-semibold hover:bg-green-600";
    } else {
      cell.className = "bg-red-100 text-red-400 rounded py-2 cursor-not-allowed";
      cell.disabled = true;
    }
    cell.addEventListener("click", () => selectDate(d.date, cell));
    grid.appendChild(cell);
  }
}

async function changeMonth(delta: number) {
  const newDate = new Date(viewYear, viewMonth + delta, 1);
  viewYear = newDate.getFullYear();
  viewMonth = newDate.getMonth();
  await renderMonth();
}

async function selectDate(date: string, cell: HTMLElement) {
  state.draft.reservation_date = date;
  document.querySelectorAll("#calendar-grid button").forEach((b) => {
    (b as HTMLElement).style.outline = "";
  });
  cell.style.outline = "2px solid #047857";
  await new Promise((r) => setTimeout(r, 150));
  const { renderStep2 } = await import("./step2Time");
  renderStep2();
}

/** "YYYY-MM-DD" → "M/D(曜)"。Phase 1.5.4 拡張：reschedule バナー用 */
function formatJpDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${m}/${d}(${w})`;
}
